// apps/client/src/utils/revenueHurdle.ts
// Monthly revenue hurdle incentive (2026-10-06).
// All monetary values in integer cents. Never use floats for money.
//
// Model: the broker's tier-based split payout for a month is scaled by a
// hurdle factor (1 - hurdle / attributedRevenue). Months below the hurdle go
// NEGATIVE and net against positive months inside the financial year. This is
// deliberately NOT the draw-against base salary in drawAgainstCommission.ts:
// that primitive is a floor the company tops up to, this one is a cost
// recovery hurdle the broker must clear before the split rate applies.
//
// Handover: docs/handovers/2026-10-06-COMMISSION-MONTHLY-REVENUE-HURDLE.md

import { findActiveConfig } from './configResolution';

export type HurdleClawbackBucket = 'own' | 'as_tagged';
export type HurdleYearEndRule = 'carry' | 'forgive' | 'clawback';

/** The hurdle fields on a commission config. All optional; absent = no hurdle. */
export interface RevenueHurdleConfig {
  effectiveFrom: Date;
  effectiveTo?: Date | null;
  /** Monthly attributed revenue the broker must clear. null = no hurdle. */
  monthlyRevenueHurdleCents?: number | null;
  /** Where clawback rows land in the split payout. `own` reprices every
   *  clawback at the broker's own-deal upfront rate (the tenant calculator
   *  puts all clawbacks in the own bucket); `as_tagged` keeps the share each
   *  row already carries. Default `own`. */
  hurdleClawbackBucket?: HurdleClawbackBucket | null;
  /** What happens to a negative financial-year total. Default `forgive`. */
  hurdleYearEndRule?: HurdleYearEndRule | null;
  /** First month of the financial year, 1 to 12. Default 6 (June), the
   *  tenant calculator's year. */
  hurdleFiscalYearStartMonth?: number | null;
  /** Own-deal upfront rate, used only by the `own` clawback bucket. */
  upfrontSelfSourcedPct?: number | null;
}

export const DEFAULT_HURDLE_CLAWBACK_BUCKET: HurdleClawbackBucket = 'own';
export const DEFAULT_HURDLE_YEAR_END_RULE: HurdleYearEndRule = 'forgive';
export const DEFAULT_HURDLE_FISCAL_YEAR_START_MONTH = 6;

/** The minimum a payment row must expose. Both commission pages' LoanTagRow
 *  shapes satisfy it. */
export interface HurdleRow {
  month: string; // 'YYYY-MM' or longer; first 7 chars are used
  paymentType: string;
  /** Gross commission on the row, signed as stored. */
  commissionCents: number;
  introductionType: 'broker' | 'company' | 'self_sourced' | null;
  isArrears: boolean;
  isDischarged: boolean;
  isReferrerDeduction: boolean;
}

export interface HurdleMonth {
  month: string;
  /** Upfronts on every tier, trail on own deals only, less clawbacks and
   *  referrer fees. Can be negative. */
  attributedRevenueCents: number;
  /** The hurdle in force for the month; null when no hurdle applied. */
  hurdleCents: number | null;
  /** (attributedRevenue - hurdle) / attributedRevenue. null when the hurdle
   *  could not be applied (no hurdle configured, or revenue <= 0). */
  hurdleFactor: number | null;
  /** Tier-based payout before the hurdle. */
  splitPayoutCents: number;
  /** splitPayout x hurdleFactor, or splitPayout when no hurdle applied. */
  monthlyIncentiveCents: number;
  /** Negative balance carried in from the previous financial year under the
   *  `carry` rule. 0 otherwise. */
  openingCarryCents: number;
  /** Running financial-year total including the opening carry. */
  fyToDateCents: number;
  /** Financial year key, e.g. 2027 for Jun 2026 to May 2027 (start month 6). */
  fiscalYear: number;
  /** Set on the last month of a financial year that closed negative under the
   *  `clawback` rule: the amount owed back by the broker. */
  yearEndClawbackCents: number;
  hurdleApplied: boolean;
  note: string;
}

export interface HurdleScheduleOptions<T extends HurdleRow> {
  /** The share basis each page reconciles on: brokerNetCents on the admin
   *  page, brokerShareCents on the broker's own page. */
  shareOf: (row: T) => number;
  /** Which months to produce. Defaults to every month present in rows. */
  months?: string[];
}

function pctOf(cents: number, pct: number): number {
  return Math.round((cents * pct) / 100);
}

export function fiscalYearFor(month: string, startMonth: number): number {
  const [y, m] = month.slice(0, 7).split('-').map(Number);
  // A year labelled by the calendar year it ENDS in, matching fiscalYearOf()
  // in commissionTree.ts (start month 7 gives the Australian FY).
  return startMonth === 1 ? y : m >= startMonth ? y + 1 : y;
}

/** Does any config in the history carry a hurdle? Lets a page skip the
 *  schedule entirely so brokers without one see byte-identical output. */
export function hasRevenueHurdle(configs: ReadonlyArray<RevenueHurdleConfig>): boolean {
  return configs.some(
    c => typeof c.monthlyRevenueHurdleCents === 'number' && c.monthlyRevenueHurdleCents > 0,
  );
}

/**
 * Attributed revenue and split payout for ONE month's rows.
 *
 * Revenue rules (the tenant calculator's rows 6 to 11):
 * - upfront: counts on every tier.
 * - trail: counts only on own (self_sourced) deals. Company trail is never
 *   revenue to the broker.
 * - clawback: signed as stored, reduces revenue.
 * - referrer deduction: reduces revenue by its magnitude. It NEVER enters the
 *   payout: the company bears it (2026-08-15 referrer fee invariant).
 * - arrears / discharged status rows: neither revenue nor payout.
 *
 * Payout rules: every row contributes `shareOf(row)` except clawbacks under
 * the `own` bucket, which are repriced at the own-deal upfront rate.
 */
export function computeHurdleMonthInputs<T extends HurdleRow>(
  rows: ReadonlyArray<T>,
  shareOf: (row: T) => number,
  clawbackBucket: HurdleClawbackBucket,
  upfrontSelfSourcedPct: number,
): { attributedRevenueCents: number; splitPayoutCents: number } {
  let revenue = 0;
  let payout = 0;
  for (const row of rows) {
    if (row.isArrears || row.isDischarged) continue;
    if (row.isReferrerDeduction) {
      revenue -= Math.abs(row.commissionCents);
      continue;
    }
    if (row.paymentType === 'clawback') {
      revenue += row.commissionCents;
      payout +=
        clawbackBucket === 'own'
          ? pctOf(row.commissionCents, upfrontSelfSourcedPct)
          : shareOf(row);
      continue;
    }
    if (row.paymentType === 'upfront') {
      revenue += row.commissionCents;
      payout += shareOf(row);
      continue;
    }
    // trail and every other commission leg
    if (row.introductionType === 'self_sourced') revenue += row.commissionCents;
    payout += shareOf(row);
  }
  return { attributedRevenueCents: revenue, splitPayoutCents: payout };
}

/** splitPayout scaled by (revenue - hurdle) / revenue, in integer cents. */
export function applyHurdle(
  splitPayoutCents: number,
  attributedRevenueCents: number,
  hurdleCents: number,
): { monthlyIncentiveCents: number; hurdleFactor: number | null } {
  if (attributedRevenueCents <= 0) {
    // The calculator divides by zero here. Nothing to scale: report the
    // split payout unchanged and flag it so the page can say so.
    return { monthlyIncentiveCents: splitPayoutCents, hurdleFactor: null };
  }
  const hurdleFactor = (attributedRevenueCents - hurdleCents) / attributedRevenueCents;
  return {
    monthlyIncentiveCents: Math.round(
      (splitPayoutCents * (attributedRevenueCents - hurdleCents)) / attributedRevenueCents,
    ),
    hurdleFactor,
  };
}

/**
 * Full month-by-month schedule with the financial-year accumulator.
 *
 * Months are resolved against the config history point-in-time, the same way
 * the base salary reconciliation does, so a hurdle introduced in a later
 * config never restates earlier months.
 */
export function computeHurdleSchedule<T extends HurdleRow>(
  rows: ReadonlyArray<T>,
  configs: ReadonlyArray<RevenueHurdleConfig>,
  options: HurdleScheduleOptions<T>,
): HurdleMonth[] {
  const byMonth = new Map<string, T[]>();
  for (const row of rows) {
    const key = row.month.slice(0, 7);
    const bucket = byMonth.get(key);
    if (bucket) bucket.push(row);
    else byMonth.set(key, [row]);
  }
  const months = (options.months ?? Array.from(byMonth.keys())).slice().sort();

  const out: HurdleMonth[] = [];
  let fyToDate = 0;
  let currentFy: number | null = null;
  let openingCarry = 0;

  for (let i = 0; i < months.length; i += 1) {
    const month = months[i];
    const cfg = findActiveConfig(month, configs as RevenueHurdleConfig[]);
    const hurdle =
      cfg && typeof cfg.monthlyRevenueHurdleCents === 'number' && cfg.monthlyRevenueHurdleCents > 0
        ? cfg.monthlyRevenueHurdleCents
        : null;
    const bucket = cfg?.hurdleClawbackBucket ?? DEFAULT_HURDLE_CLAWBACK_BUCKET;
    const yearEndRule = cfg?.hurdleYearEndRule ?? DEFAULT_HURDLE_YEAR_END_RULE;
    const fyStart = cfg?.hurdleFiscalYearStartMonth ?? DEFAULT_HURDLE_FISCAL_YEAR_START_MONTH;
    const fy = fiscalYearFor(month, fyStart);

    if (currentFy !== null && fy !== currentFy) {
      // Year boundary. The previous year's closing balance decides the carry.
      openingCarry = yearEndRule === 'carry' && fyToDate < 0 ? fyToDate : 0;
      fyToDate = openingCarry;
    } else if (currentFy === null) {
      openingCarry = 0;
    }
    currentFy = fy;

    const { attributedRevenueCents, splitPayoutCents } = computeHurdleMonthInputs(
      byMonth.get(month) ?? [],
      options.shareOf,
      bucket,
      cfg?.upfrontSelfSourcedPct ?? 0,
    );

    let monthlyIncentiveCents = splitPayoutCents;
    let hurdleFactor: number | null = null;
    let hurdleApplied = false;
    let note = '';
    if (hurdle !== null) {
      const applied = applyHurdle(splitPayoutCents, attributedRevenueCents, hurdle);
      monthlyIncentiveCents = applied.monthlyIncentiveCents;
      hurdleFactor = applied.hurdleFactor;
      hurdleApplied = applied.hurdleFactor !== null;
      if (!hurdleApplied) note = 'No attributable revenue this month. Hurdle not applied.';
      else if (monthlyIncentiveCents < 0) note = 'Below hurdle. Nets against the financial year.';
    }
    fyToDate += monthlyIncentiveCents;

    const nextMonth = months[i + 1];
    const nextFy = nextMonth ? fiscalYearFor(nextMonth, fyStart) : null;
    const closesYear = nextFy !== null && nextFy !== fy;
    const yearEndClawbackCents =
      closesYear && yearEndRule === 'clawback' && fyToDate < 0 ? -fyToDate : 0;
    if (yearEndClawbackCents > 0) note = 'Financial year closed below zero. Amount owed back.';
    if (closesYear && yearEndRule === 'forgive' && fyToDate < 0) {
      note = 'Financial year closed below zero. Shortfall forgiven.';
    }

    out.push({
      month,
      attributedRevenueCents,
      hurdleCents: hurdle,
      hurdleFactor,
      splitPayoutCents,
      monthlyIncentiveCents,
      openingCarryCents: i === 0 || fiscalYearFor(months[i - 1], fyStart) !== fy ? openingCarry : 0,
      fyToDateCents: fyToDate,
      fiscalYear: fy,
      yearEndClawbackCents,
      hurdleApplied,
      note,
    });
  }
  return out;
}
